import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { trpc } from '@/api/trpc';
import Button from '@/components/UI/Button';
import FormInput from '@/components/UI/FormInput';
import FormNumberInput from '@/components/UI/FormNumberInput';
import Alert from '@/components/UI/Alert';
import LoadingSpinner from '@/components/Layout/LoadingSpinner';
import { formatTimeHorizon, getEventHorizonTooltip, isEventHorizon } from '@/utils/timeHorizonGenerator';

interface CreateScorecardWizardProps {
    strategyId: number;
    onComplete: (scorecardId: number) => void;
    onCancel: () => void;
}

export const CreateScorecardWizard: React.FC<CreateScorecardWizardProps> = ({ strategyId, onComplete, onCancel }) => {
    const [step, setStep] = useState<'experiment' | 'duration' | 'config'>('experiment');
    const [selectedExperiment, setSelectedExperiment] = useState<{ id: number; name: string } | null>(null);
    const [selectedBaselineExperiment, setSelectedBaselineExperiment] = useState<{ id: number; name: string } | null>(null);
    const [selectedDuration, setSelectedDuration] = useState<string>('');
    const [selectedHorizon, setSelectedHorizon] = useState<number>(0);

    // Config state
    const [name, setName] = useState('');
    const [targetMetric, setTargetMetric] = useState<'stock' | 'option'>('option');
    const [minPredictedProfit, setMinPredictedProfit] = useState(5.0);
    const [minMatchedRules, setMinMatchedRules] = useState(2);

    // Fetch completed experiments for this strategy
    const {
        data: experiments,
        isLoading: experimentsLoading,
        error: experimentsError
    } = useQuery({
        ...trpc.experiment.list.queryOptions({
            executionStatus: 'completed',
            limit: 100,
            offset: 0
        }),
        select: (response) => {
            // Filter experiments by strategyId client-side
            return response.experiments.filter((exp) => exp.strategyId === strategyId);
        }
    });

    // Fetch durations and horizons for selected experiment
    const {
        data: durationsAndHorizons,
        isLoading: durationsLoading,
        error: durationsError
    } = useQuery({
        ...trpc.experiment.getDurationsAndHorizons.queryOptions({
            experimentId: selectedExperiment?.id ?? 0
        }),
        enabled: !!selectedExperiment?.id
    });

    // Create scorecard mutation
    const createScorecard = useMutation({
        ...trpc.scorecards.create.mutationOptions(),
        onSuccess: (data) => {
            onComplete(data.id);
        }
    });

    const handleCreateScorecard = () => {
        if (!selectedExperiment || !selectedBaselineExperiment || !selectedDuration || !selectedHorizon) {
            return;
        }

        createScorecard.mutate({
            name,
            strategyId,
            experimentId: selectedExperiment.id,
            baselineExperimentId: selectedBaselineExperiment.id,
            duration: selectedDuration,
            horizonSeconds: selectedHorizon,
            targetMetric,
            minPredictedProfit,
            thresholds: {
                minMatchedRules
            }
        });
    };

    if (step === 'experiment') {
        return (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold mb-2">Step 1: Select Experiments</h3>
                    <p className="text-sm text-gray-600 mb-4">
                        Choose two experiments:
                        <br />
                        1) Rules Experiment (for rule creation and linking)
                        <br />
                        2) Baseline Experiment (for entity baselines)
                        <br />
                        They can be the same, but both selections are required.
                    </p>
                </div>

                {experimentsLoading ? (
                    <LoadingSpinner />
                ) : experimentsError ? (
                    <Alert variant="danger">Failed to load experiments: {experimentsError.message}</Alert>
                ) : !experiments || experiments.length === 0 ? (
                    <Alert variant="warning">No completed experiments found for this strategy. Please run an experiment first.</Alert>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700" htmlFor="experiment-select">
                                    Rules Experiment
                                </label>
                                <select
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                    id="experiment-select"
                                    value={selectedExperiment?.id?.toString() || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;

                                        const exp = experiments?.find(
                                            (experiment: { id: number; name: string }) => experiment.id === Number(value)
                                        );

                                        if (exp) {
                                            setSelectedExperiment({ id: exp.id, name: exp.name });
                                            // Reset duration and horizon when experiment changes
                                            setSelectedDuration('');
                                            setSelectedHorizon(0);
                                        } else {
                                            setSelectedExperiment(null);
                                            setSelectedDuration('');
                                            setSelectedHorizon(0);
                                        }
                                    }}
                                >
                                    <option value="">Select rules experiment...</option>
                                    {experiments?.map(
                                        (exp: {
                                            id: number;
                                            name: string;
                                            entityCount: number;
                                            durationCount: number;
                                            timeHorizonCount: number;
                                        }) => (
                                            <option key={exp.id} value={exp.id}>
                                                #{exp.id} - {exp.name} ({exp.entityCount} entities, {exp.durationCount} durations,{' '}
                                                {exp.timeHorizonCount} horizons)
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700" htmlFor="baseline-experiment-select">
                                    Baseline Experiment
                                </label>
                                <select
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                    id="baseline-experiment-select"
                                    value={selectedBaselineExperiment?.id?.toString() || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;

                                        const exp = experiments?.find(
                                            (experiment: { id: number; name: string }) => experiment.id === Number(value)
                                        );

                                        if (exp) {
                                            setSelectedBaselineExperiment({ id: exp.id, name: exp.name });
                                        } else {
                                            setSelectedBaselineExperiment(null);
                                        }
                                    }}
                                >
                                    <option value="">Select baseline experiment...</option>
                                    {experiments?.map(
                                        (exp: {
                                            id: number;
                                            name: string;
                                            entityCount: number;
                                            durationCount: number;
                                            timeHorizonCount: number;
                                        }) => (
                                            <option key={exp.id} value={exp.id}>
                                                #{exp.id} - {exp.name} ({exp.entityCount} entities, {exp.durationCount} durations,{' '}
                                                {exp.timeHorizonCount} horizons)
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-between">
                    <Button variant="secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button disabled={!selectedExperiment || !selectedBaselineExperiment} onClick={() => setStep('duration')}>
                        Next
                    </Button>
                </div>
            </div>
        );
    }

    if (step === 'duration') {
        const availableDurations = durationsAndHorizons?.durations || [];
        const horizonsForDuration = selectedDuration ? durationsAndHorizons?.durationHorizonMap[selectedDuration] || [] : [];

        return (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold mb-2">Step 2: Select Duration and Horizon</h3>
                    <div className="bg-gray-50 p-3 rounded text-sm mb-4">
                        <p className="font-medium">Selected Experiment:</p>
                        <p>{selectedExperiment?.name}</p>
                    </div>
                </div>

                {durationsLoading ? (
                    <LoadingSpinner />
                ) : durationsError ? (
                    <Alert variant="danger">Failed to load durations and horizons: {durationsError.message}</Alert>
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700" htmlFor="duration-select">
                                Duration
                            </label>
                            <select
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                id="duration-select"
                                value={selectedDuration}
                                onChange={(e) => {
                                    setSelectedDuration(e.target.value);
                                    setSelectedHorizon(0); // Reset horizon when duration changes
                                }}
                            >
                                <option value="">Select a duration...</option>
                                {availableDurations.map((duration: string) => (
                                    <option key={duration} value={duration}>
                                        {duration}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedDuration && (
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700" htmlFor="horizon-select">
                                    Time Horizon
                                </label>
                                <select
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                    id="horizon-select"
                                    value={selectedHorizon.toString()}
                                    onChange={(e) => setSelectedHorizon(Number(e.target.value))}
                                >
                                    <option value="0">Select a horizon...</option>
                                    {horizonsForDuration.map((horizon: number) => {
                                        const label = formatTimeHorizon(horizon);
                                        const tooltip = isEventHorizon(horizon) ? getEventHorizonTooltip(horizon) : null;
                                        const isOffset = horizon > 0;
                                        const days = isOffset ? horizon / 86400 : 0;

                                        return (
                                            <option key={horizon} title={tooltip ?? undefined} value={horizon}>
                                                {label}
                                                {isOffset && days >= 1 ? ` (${days.toFixed(1)} days)` : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-between">
                    <Button variant="secondary" onClick={() => setStep('experiment')}>
                        Back
                    </Button>
                    <Button disabled={!selectedDuration || !selectedHorizon} onClick={() => setStep('config')}>
                        Next
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold mb-2">Step 3: Configure Scorecard</h3>
                <div className="bg-gray-50 p-3 rounded text-sm">
                    <p className="font-medium">Linked Experiment:</p>
                    <p>{selectedExperiment?.name}</p>
                    <p className="text-gray-600">
                        Duration: {selectedDuration} | Horizon: {formatTimeHorizon(selectedHorizon)}
                        {selectedHorizon / 86400 >= 1 ? ` (${(selectedHorizon / 86400).toFixed(1)} days)` : ''}
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <FormInput
                    required
                    label="Scorecard Name"
                    placeholder="e.g., MACD Crossover Options Strategy"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />

                <div className="space-y-2">
                    <div className="block text-sm font-medium text-gray-700">Target Metric</div>
                    <div className="space-y-2">
                        <label className="flex items-center">
                            <input
                                checked={targetMetric === 'stock'}
                                className="mr-2"
                                name="targetMetric"
                                type="radio"
                                value="stock"
                                onChange={(e) => setTargetMetric(e.target.value as 'stock' | 'option')}
                            />
                            <span>Stock Returns</span>
                        </label>
                        <label className="flex items-center">
                            <input
                                checked={targetMetric === 'option'}
                                className="mr-2"
                                name="targetMetric"
                                type="radio"
                                value="option"
                                onChange={(e) => setTargetMetric(e.target.value as 'stock' | 'option')}
                            />
                            <span>Option Returns</span>
                        </label>
                    </div>
                </div>

                <FormNumberInput
                    label="Minimum Predicted Profit (%)"
                    min={0}
                    step={0.1}
                    value={minPredictedProfit}
                    onValueChange={(value) => {
                        // Required thresholds keep their stored value while the field is empty.
                        if (value === null) return;

                        setMinPredictedProfit(value);
                    }}
                />

                <FormNumberInput
                    integer
                    label="Minimum Matched Rules"
                    min={0}
                    value={minMatchedRules}
                    onValueChange={(value) => {
                        if (value === null) return;

                        setMinMatchedRules(value);
                    }}
                />
            </div>

            <div className="flex justify-between">
                <Button variant="secondary" onClick={() => setStep('duration')}>
                    Back
                </Button>
                <Button disabled={!name || createScorecard.isPending} isLoading={createScorecard.isPending} onClick={handleCreateScorecard}>
                    Create Scorecard
                </Button>
            </div>

            {createScorecard.isError && <Alert variant="danger">{createScorecard.error?.message || 'Failed to create scorecard'}</Alert>}
        </div>
    );
};
